import { LightObject, LightModel, BookingDB } from "./BookingLightDB"
import { computed, observable, action } from "mobx"
import {
  uuid,
  isValidPhone,
  isValidEmail,
  cleanAddress,
  checkForLastNameInFirstName
} from "../../light/utils"
import dayjs = require("dayjs")
import { SquareCustomer } from "light/light-square"
import { Waiver, $Waiver } from "./Waiver"
const { parseNumber } = require("libphonenumber-js")

export interface $Member extends LightObject {
  firstName: string
  lastName: string
  dobDay: string
  dobMonth: string
  dobYear: string
  email?: string
  phone?: string
  street?: string
  city?: string
  state?: string
  zip?: string
  sqCustId?: string
  $memberMinors?: { [$id: string]: string }
  $waivers?: { [$id: string]: string }
  $bookings?: { [$id: string]: string }
}

export class Member implements LightModel {
  constructor(db: BookingDB, object?: $Member) {
    this.db = db
    this.object = object ? object : Member.create$Member()
  }

  @computed
  get type() {
    return this.object.$type
  }

  db: BookingDB

  @computed
  get children() {
    return this.minors
  }

  static create$Member(): $Member {
    return {
      $id: uuid(),
      $type: "member",
      $version: uuid(),
      $parentVersion: null,
      $versionDateString: new Date().toISOString(),
      firstName: "",
      lastName: "",
      dobDay: "",
      dobMonth: "",
      dobYear: "",
      email: "",
      phone: "",
      street: "",
      city: "",
      state: "",
      zip: ""
    }
  }

  @observable
  object: $Member

  @computed
  get minors(): Member[] {
    if (this.object.$memberMinors)
      return Object.keys(this.object.$memberMinors).map(k => {
        const object = this.db.file.objects[k]
        return new Member(this.db, object as $Member)
      })
    return []
  }

  @computed
  get waivers(): Waiver[] {
    if (this.object.$waivers)
      return Object.keys(this.object.$waivers).map(k => {
        const object: $Waiver = this.db.file.objects[k] as $Waiver
        const ownerId: string = object.$memberAdult as string
        return new Waiver(this.db, object, ownerId)
      })
    return []
  }

  @computed
  get lookupKeys(): string[] {
    return this.isAdult ? ["email", "phone", "uniqueIndex"] : ["uniqueIndex"]
  }

  @computed
  get existingObjectIdInLookup(): string | undefined {
    for (var i = 0; i < this.lookupValues.length; i++) {
      const lookupValue = this.lookupValues[i]
      const existing = this.db.file.memberLookup[lookupValue]
      const foundId =
        existing != null && existing != this.id ? existing : undefined
      if (foundId) {
        return foundId
      }
    }

    return undefined
  }

  @computed
  get fullName() {
    if (!this.object.firstName && !this.object.lastName) return null
    return `${this.object.firstName} ${this.object.lastName}`
  }

  @computed
  get shortName() {
    if (!this.object.firstName && !this.object.lastName) return null
    return `${this.object.firstName} ${this.object.lastName.substr(0, 1)}.`
  }

  @computed
  get isValidFirstName() {
    return this.object.firstName ? this.object.firstName.length > 0 : false
  }

  @computed
  get isValidLastName() {
    return this.object.lastName ? this.object.lastName.length > 0 : false
  }

  @computed
  get isValidName() {
    return this.isValidFirstName && this.isValidLastName
  }

  @computed
  get dobFriendlyString() {
    return (
      this.object.dobMonth +
      " " +
      this.object.dobDay +
      " " +
      this.object.dobYear
    )
  }

  @computed
  get isAdult() {
    return !this.isMinor
  }

  @computed
  get isValidMinor() {
    return this.isValidBasic && this.isMinor
  }

  @computed
  get isMinor() {
    if (!this.isValidDOB) return false

    const today = dayjs()

    var dobPlusEighteen = this.dobRaw.add(18, "year")
    if (
      dobPlusEighteen.isBefore(today) ||
      dobPlusEighteen.isSame(today)
    ) {
      return false
    }
    return true
  }

  @computed
  get addressFriendlyWithoutZip() {
    return `${this.object.street} ${this.object.city} ${this.object.state}`
  }

  @computed
  get isValidEmail() {
    return isValidEmail(this.object.email)
  }

  @computed
  get isValidPhone(): boolean {
    return isValidPhone(this.object.phone)
  }

  @computed
  get isValidStreet() {
    return this.object.street ? this.object.street.length > 0 : false
  }

  @computed
  get isValidCity() {
    return this.object.city ? this.object.city.length > 0 : false
  }

  @computed
  get isValidState() {
    return this.object.state ? this.object.state.length === 2 : false
  }

  @computed
  get isValidZip() {
    if (!this.object.zip) return false
    let zipAsNumber = parseInt(this.object.zip)
    return zipAsNumber.toString().split("").length === 5 && !isNaN(zipAsNumber)
  }

  @observable
  shouldCheckAddress: boolean = true

  @computed
  get isValidAddress() {
    if (this.shouldCheckAddress === false) return true
    return (
      this.isValidStreet &&
      this.isValidCity &&
      this.isValidState &&
      this.isValidZip
    )
  }

  @computed
  get invalidMessage(): string | undefined {
    if (!this.isValidName) {
      return "Please enter your first and last name."
    } else if (!this.isValidDOB) {
      return "Please enter a valid date of birth."
    } else if (this.isAdult && !this.isValidEmail) {
      return "Please enter a valid email."
    } else if (this.isAdult && !this.isValidPhone) {
      return "Please enter a valid phone."
    } else if (this.isAdult && !this.isValidAddress) {
      return "Please enter a valid address."
    } else if (this.existingObjectIdInLookup) {
      return "This member already exists"
    }
    return undefined
  }

  @computed
  get uniqueIndex(): string | undefined {
    if (!this.object.firstName || !this.object.lastName || !this.dobIndex)
      return undefined
    return (
      this.object.firstName.toLowerCase() +
      ":" +
      this.object.lastName.toLowerCase() +
      ":" +
      this.dobIndex
    )
  }

  isValidUniqueIndex() {
    return this.uniqueIndex && this.uniqueIndex.length > 6
  }

  @computed
  get dob(): string | null {
    return this.dobRaw ? this.dobRaw.toISOString() : null
  }

  @computed
  get dobIndex() {
    return this.dobRaw ? this.dobRaw.format("MM:DD:YYYY") : null
  }

  @computed
  get dobString() {
    return (
      this.object.dobMonth +
      "/" +
      this.object.dobDay +
      "/" +
      this.object.dobYear
    )
  }

  @computed
  get dobRaw() {
    if (this.dobString) return dayjs(this.dobString)
    return dayjs("Invalid")
  }

  @computed
  get isValidDOB() {
    const answer =
      this.dobRaw.isValid() &&
      this.dobRaw.isBefore(dayjs()) &&
      parseInt(this.object.dobYear) > 1900
    return answer
  }

  @computed
  get isValidBasic() {
    const a =
      this.isValidName &&
      !this.existingObjectIdInLookup &&
      this.object.dobYear.length > 0 &&
      this.object.dobMonth.length > 0 &&
      this.isValidDOB
    return a
  }

  @computed
  get isValidAdult() {
    return (
      this.isValidBasic &&
      !this.isMinor &&
      this.isValidAddress &&
      this.isValidEmail &&
      this.isValidPhone
    )
  }

  @computed
  get isValid(): boolean {
    return this.isMinor ? this.isValidBasic : this.isValidAdult
  }

  @computed
  get toSquareCustomer(): SquareCustomer {
    if (!this.isValidAdult)
      throw new Error(
        "Only valid adult members can be converted to square customers"
      )

    return {
      given_name: this.object.firstName,
      family_name: this.object.lastName,
      email_address: this.object.email,
      phone_number: this.object.phone,
      address: {
        address_line_1: this.object.street,
        address_line_2: "",
        locality: this.object.state,
        country: "US",
        postal_code: this.object.zip
      },
      reference_id: this.object.$id,
      note: `${this.fullName} via Dotcom Light Booking`
    }
  }

  @action.bound
  addWaiver(waiver: Waiver) {
    if (!this.object.$waivers) this.object.$waivers = {}
    if (waiver.isValid) {
      this.object.$waivers[waiver.object.$id] = waiver.object.$id
    } else throw new Error("Cannot add invalid waiver to booking")
  }

  @computed
  get id() {
    return this.object.$id
  }

  @computed
  get lookupValues() {
    const lookupValues: string[] = []
    if (this.isAdult) {
      if (this.object.phone && this.isValidPhone)
        lookupValues.push(this.object.phone)
      if (this.object.email && this.isValidEmail)
        lookupValues.push(this.object.email)
    }
    if (this.uniqueIndex && this.isValidUniqueIndex)
      lookupValues.push(this.uniqueIndex)
    return lookupValues
  }

  @action.bound
  clean() {
    const object = this.object
    if (object.firstName) object.firstName = object.firstName.trim()
    if (object.lastName) object.firstName = object.firstName.trim()
    if (object.email) object.email = object.email.trim().toLowerCase()
    if (object.firstName && object.lastName) checkForLastNameInFirstName(object)

    if (object.phone) {
      const parsedPhone = parseNumber(String(object.phone), "US").phone
      if (parsedPhone) object.phone = parsedPhone
    }
    cleanAddress(object)
  }

  @action.bound
  cleanAndCheck() {
    this.clean()
    if (!this.isValid) {
      throw new Error(this.invalidMessage)
    }
    this.minors.forEach(m => m.cleanAndCheck())
  }

  @action.bound
  addMinor(minor: Member) {
    if (minor.isValidMinor) {
      if (!this.object.$memberMinors) this.object.$memberMinors = {}
      this.object.$memberMinors[minor.object.$id] = minor.object.$id
    } else throw new Error("Cannot add invalid minor to member")
  }
}
